import React, { useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import qwerLogoUrl from "./logo/qwer.webp";
import "./streamingImage.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const MAX_IMAGE_DIMENSION = 4000;
const MAX_CANVAS_SIZE = 2400;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const SELECTION_COLOR = "#3DD6F0";
const MAX_TEXTS = 8; // 문구 요소 최대 개수

const isIOS =
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

// 고정 레이어. 문구 요소는 layout.texts 배열이며 id 가 "text:..." 로 시작
const FIXED_LAYERS = ["left", "right", "logo"];
const FIXED_LABELS = {
  left: "좌측 사진",
  right: "우측 사진",
  logo: "로고",
};
const isTextId = (id) => typeof id === "string" && id.startsWith("text:");

// 문구를 플랫폼별로 브라우저(localStorage)에 저장
const PLATFORMS = [
  { key: "naver", label: "네이버카페" },
  { key: "x", label: "트위터(X)" },
  { key: "threads", label: "쓰레드" },
  { key: "etc", label: "기타" },
];
const STORAGE_PLATFORM_KEY = "smi.platform";
const nickStorageKey = (platform) => `smi.nick.${platform}`;
const storageGet = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const storageSet = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* 시크릿 모드 등 저장 불가 환경은 무시 */
  }
};

const TEXT_PRESETS = [
  { label: "흰색", color: "#ffffff" },
  { label: "검정", color: "#000000" },
  { label: "핑크", color: "#e98db6" },
  { label: "하늘", color: "#3dd6f0" },
  { label: "연두", color: "#c7d86b" },
];

let textSeq = 0;
const newTextId = () => `text:${Date.now().toString(36)}-${(textSeq += 1)}`;

const nowString = () => dayjs().tz("Asia/Seoul").format("YYYY-MM-DD HH:mm");

// 문구 요소: 닉네임(nick)과 시간(time)이 한 줄 "@닉네임 / 시간" 으로 그려진다.
// 시간은 닉네임을 넣으면 자동으로 붙고, "시간 삭제" 를 누르면 timeRemoved 로 다시 붙지 않는다
const createTextEntry = (y = 0.92) => ({
  id: newTextId(),
  nick: "",
  time: "",
  timeRemoved: false,
  x: 0.5,
  y,
  size: 0.045,
  color: "#ffffff",
  outline: true,
});
const createPhotoLayer = () => ({ zoom: 1, dx: 0, dy: 0 });

// 문구 앞 "@" 는 입력창에는 보이지 않고 이미지에 그릴 때만 붙인다 (atPrefix 켜진 경우).
// 사용자가 직접 "@" 를 넣었으면 중복되지 않게 하나로 정리
const withAtPrefix = (raw) => {
  const body = String(raw || "").replace(/^[\s@]+/, "");
  return body ? `@${body}` : "";
};
const formatTextEntry = (entry, atPrefix) => {
  const nick = atPrefix ? withAtPrefix(entry.nick) : String(entry.nick || "").trim();
  const time = String(entry.time || "").trim();
  return [nick, time].filter(Boolean).join(" / ");
};

const createDefaultLayout = () => ({
  logo: { x: 0.5, y: 0.5, size: 0.5, opacity: 1, visible: true }, // visible: 로고 표시 여부
  left: createPhotoLayer(),
  right: createPhotoLayer(),
  texts: [createTextEntry()],
  atPrefix: true, // 문구 맨 앞 "@" 자동 추가 (모든 문구 요소 공통)
});

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const imgW = (img) => img.naturalWidth || img.width;
const imgH = (img) => img.naturalHeight || img.height;

// 레이어 조회/수정 헬퍼 (고정 레이어와 문구 요소를 같은 방식으로 다룸)
const getLayer = (layout, id) =>
  isTextId(id) ? layout.texts.find((t) => t.id === id) : layout[id];
const patchLayout = (prev, id, patch) => {
  const cur = getLayer(prev, id);
  if (!cur) return prev;
  const next = { ...cur, ...(typeof patch === "function" ? patch(cur) : patch) };
  if (isTextId(id)) {
    return { ...prev, texts: prev.texts.map((t) => (t.id === id ? next : t)) };
  }
  return { ...prev, [id]: next };
};

const contrastColor = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return "#000000";
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? "#000000" : "#ffffff";
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const downscaleImage = async (img) => {
  const w = imgW(img);
  const h = imgH(img);
  if (w <= MAX_IMAGE_DIMENSION && h <= MAX_IMAGE_DIMENSION) return img;
  const scale = Math.min(MAX_IMAGE_DIMENSION / w, MAX_IMAGE_DIMENSION / h);
  const off = document.createElement("canvas");
  off.width = Math.round(w * scale);
  off.height = Math.round(h * scale);
  off.getContext("2d").drawImage(img, 0, 0, off.width, off.height);
  return loadImage(off.toDataURL("image/jpeg", 0.92));
};

const isHeicByMagic = async (file) => {
  try {
    const buf = await file.slice(0, 12).arrayBuffer();
    const bytes = new Uint8Array(buf);
    const sig = String.fromCharCode(...bytes.slice(4, 12));
    return /ftyp(heic|heix|hevc|mif1|msf1)/.test(sig);
  } catch {
    return false;
  }
};

// 좌/우 사진 원본 비율을 유지한 채 캔버스 크기와 각 슬롯 폭을 계산
const computeGeometry = (left, right) => {
  const lh = imgH(left);
  const rh = imgH(right);
  let height = Math.max(lh, rh);
  let leftW = imgW(left) * (height / lh);
  let rightW = imgW(right) * (height / rh);
  let width = leftW + rightW;
  if (width > MAX_CANVAS_SIZE || height > MAX_CANVAS_SIZE) {
    const s = Math.min(MAX_CANVAS_SIZE / width, MAX_CANVAS_SIZE / height);
    width *= s;
    height *= s;
    leftW *= s;
  }
  width = Math.round(width);
  height = Math.round(height);
  leftW = Math.round(leftW);
  return { width, height, leftW, rightW: width - leftW };
};

const drawImageCover = (ctx, img, x, y, w, h) => {
  const iw = imgW(img);
  const ih = imgH(img);
  const imgRatio = iw / ih;
  const targetRatio = w / h;
  let sw = iw;
  let sh = ih;
  let sx = 0;
  let sy = 0;
  if (imgRatio > targetRatio) {
    sw = ih * targetRatio;
    sx = (iw - sw) / 2;
  } else {
    sh = iw / targetRatio;
    sy = (ih - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
};

// 슬롯 안에서 zoom 배율로 확대하고 dx/dy(슬롯 폭·높이 비율)만큼 이동해 그리기
const drawPhoto = (ctx, img, slot, layer) => {
  ctx.save();
  ctx.beginPath();
  ctx.rect(slot.x, slot.y, slot.w, slot.h);
  ctx.clip();
  const w = slot.w * layer.zoom;
  const h = slot.h * layer.zoom;
  const x = slot.x + (slot.w - w) / 2 + layer.dx * slot.w;
  const y = slot.y + (slot.h - h) / 2 + layer.dy * slot.h;
  ctx.drawImage(img, x, y, w, h);
  ctx.restore();
};

const drawText = (ctx, text, layer, W, H) => {
  if (!text) return null;
  const fontSize = H * layer.size;
  const x = W * layer.x;
  const y = H * layer.y;
  ctx.save();
  ctx.font = `bold ${fontSize}px Pretendard, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  if (layer.outline) {
    ctx.strokeStyle = contrastColor(layer.color);
    ctx.lineWidth = fontSize / 4;
    ctx.lineJoin = "round";
    ctx.strokeText(text, x, y);
  }
  ctx.fillStyle = layer.color;
  ctx.fillText(text, x, y);
  const width = ctx.measureText(text).width;
  ctx.restore();
  return { x: x - width / 2, y: y - fontSize, w: width, h: fontSize * 1.2 };
};

const drawSelection = (ctx, id, boxes, W) => {
  const lw = Math.max(2, W * 0.003);
  ctx.save();
  ctx.lineWidth = lw;
  ctx.setLineDash([lw * 3, lw * 2]);
  const path = () => {
    ctx.beginPath();
    if (id === "logo") {
      if (!boxes.logo) return false;
      const { cx, cy, r } = boxes.logo;
      ctx.arc(cx, cy, r + lw, 0, Math.PI * 2);
    } else {
      const b = boxes[id];
      if (!b) return false;
      const pad = isTextId(id) ? b.h * 0.25 : -lw;
      ctx.rect(b.x - pad, b.y - pad, b.w + pad * 2, b.h + pad * 2);
    }
    return true;
  };
  if (path()) {
    ctx.strokeStyle = "rgba(255,255,255,0.9)";
    ctx.lineWidth = lw * 2;
    ctx.stroke();
    ctx.strokeStyle = SELECTION_COLOR;
    ctx.lineWidth = lw;
    ctx.stroke();
  }
  ctx.restore();
};

// 캔버스에 전체 장면을 그리고, 터치 판정용 영역(boxes)을 반환
const drawScene = (canvas, images, layout, selected) => {
  const geo = computeGeometry(images.left, images.right);
  if (canvas.width !== geo.width || canvas.height !== geo.height) {
    canvas.width = geo.width;
    canvas.height = geo.height;
  }
  const W = geo.width;
  const H = geo.height;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, W, H);

  const slots = {
    left: { x: 0, y: 0, w: geo.leftW, h: H },
    right: { x: geo.leftW, y: 0, w: geo.rightW, h: H },
  };
  drawPhoto(ctx, images.left, slots.left, layout.left);
  drawPhoto(ctx, images.right, slots.right, layout.right);

  const logo = layout.logo;
  const size = H * logo.size;
  const cx = W * logo.x;
  const cy = H * logo.y;
  const logoShown = logo.visible !== false && Boolean(images.center) && logo.opacity > 0;
  if (logoShown) {
    ctx.save();
    ctx.globalAlpha = logo.opacity;
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    drawImageCover(ctx, images.center, cx - size / 2, cy - size / 2, size, size);
    ctx.restore();
  }

  const boxes = {
    left: slots.left,
    right: slots.right,
    logo: logoShown ? { cx, cy, r: size / 2 } : null,
    textIds: layout.texts.map((t) => t.id),
  };
  for (const entry of layout.texts) {
    boxes[entry.id] = drawText(ctx, formatTextEntry(entry, layout.atPrefix), entry, W, H);
  }
  if (selected) drawSelection(ctx, selected, boxes, W);
  return boxes;
};

const hitTest = (boxes, px, py) => {
  if (!boxes) return null;
  const inRect = (r, pad = 0) =>
    r &&
    px >= r.x - pad &&
    px <= r.x + r.w + pad &&
    py >= r.y - pad &&
    py <= r.y + r.h + pad;
  // 나중에 그린(위에 있는) 문구부터 판정
  for (const id of [...boxes.textIds].reverse()) {
    const b = boxes[id];
    if (b && inRect(b, b.h * 0.5)) return id;
  }
  const l = boxes.logo;
  if (l && Math.hypot(px - l.cx, py - l.cy) <= l.r) return "logo";
  if (inRect(boxes.right)) return "right";
  if (inRect(boxes.left)) return "left";
  return null;
};

const timeSetForImageName = () =>
  "streaming_" + dayjs().tz("Asia/Seoul").format("YYYYMMDD_HHmmss");

// 스위치형 토글 (로고 표시 / @ 표시 / 시간)
const Toggle = ({ checked, onChange, label, ariaLabel }) => (
  <label className={`smi-toggle${checked ? " is-on" : ""}`}>
    <input
      type="checkbox"
      role="switch"
      checked={checked}
      aria-checked={checked}
      aria-label={ariaLabel || label}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span className="smi-toggle-track" aria-hidden="true">
      <span className="smi-toggle-knob" />
    </span>
    <span className="smi-toggle-text">{label}</span>
  </label>
);

function StreamingImageMerger() {
  const [leftImg, setLeftImg] = useState(null);
  const [rightImg, setRightImg] = useState(null);
  const [centerImg, setCenterImg] = useState(null);
  const [fileNames, setFileNames] = useState({ left: "", right: "", center: "" });
  const [layout, setLayout] = useState(createDefaultLayout);
  const [selected, setSelected] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [resultUrl, setResultUrl] = useState(null); // iOS 꾹 눌러 저장용
  const [exporting, setExporting] = useState(false);
  const [platform, setPlatform] = useState(PLATFORMS[0].key);

  const canvasRef = useRef(null);
  const boxesRef = useRef(null);
  const pointersRef = useRef(new Map()); // pointerId -> {x, y} (캔버스 좌표)
  const gestureRef = useRef(null); // {id, lastX, lastY, pinchDist, pinchBase}
  const rafRef = useRef(null);
  const pendingRef = useRef(null);

  const images = { left: leftImg, right: rightImg, center: centerImg };
  const ready = Boolean(leftImg && rightImg);

  // ---------- 파일 업로드 ----------
  const handleImageChange = async (e, key) => {
    const file = e.target.files[0];
    if (!file) return;
    const input = e.target;
    const name = file.name.toLowerCase();
    if (
      name.endsWith(".heic") ||
      name.endsWith(".heif") ||
      (await isHeicByMagic(file))
    ) {
      alert("HEIC 포맷은 지원하지 않습니다. JPG 또는 PNG 파일을 선택해 주십시오.");
      input.value = "";
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      alert("파일 크기가 10MB를 초과합니다. 더 작은 파일을 선택해 주십시오.");
      input.value = "";
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    try {
      const raw = await loadImage(objectUrl);
      if (!imgW(raw) || !imgH(raw)) {
        throw new Error("이미지 크기를 인식하지 못했습니다.");
      }
      const img = await downscaleImage(raw);
      setErrorMsg("");
      setFileNames((prev) => ({ ...prev, [key]: file.name }));
      if (key === "left") {
        setLeftImg(img);
        setLayout((prev) => ({ ...prev, left: createPhotoLayer() }));
      } else if (key === "right") {
        setRightImg(img);
        setLayout((prev) => ({ ...prev, right: createPhotoLayer() }));
      } else {
        setCenterImg(img);
      }
    } catch (err) {
      console.error("[image load] failed", {
        name: file.name,
        size: file.size,
        type: file.type,
        err,
      });
      alert(
        `이미지를 불러오지 못했습니다. (${file.name} / ${(file.size / 1024 / 1024).toFixed(1)}MB / ${file.type || "unknown"})\n` +
          `다른 사진을 시도해 주십시오. 갤럭시의 경우 카메라 설정에서 "고효율 이미지(HEIF)" 또는 "Pro 모드 RAW"를 해제 후 다시 촬영해 보십시오.`,
      );
      input.value = "";
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  };

  const swapPhotos = () => {
    if (!leftImg && !rightImg) return;
    setLeftImg(rightImg);
    setRightImg(leftImg);
    setFileNames((prev) => ({ ...prev, left: prev.right, right: prev.left }));
    setLayout((prev) => ({ ...prev, left: prev.right, right: prev.left }));
    setSelected((s) => (s === "left" ? "right" : s === "right" ? "left" : s));
  };

  // 기본 로고
  useEffect(() => {
    let cancelled = false;
    loadImage(qwerLogoUrl)
      .then((img) => {
        if (!cancelled) setCenterImg(img);
      })
      .catch((e) => console.error("[default logo] load failed", e));
    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- 플랫폼별 문구 저장/복원 ----------
  // 닉네임이 채워지고 시간이 비어 있으면(삭제한 적 없으면) 현재 시간을 자동으로 붙인다
  const withAutoTime = (entry, nick) => {
    const next = { ...entry, nick };
    if (!nick.trim()) next.timeRemoved = false;
    else if (!entry.time && !entry.timeRemoved) next.time = nowString();
    return next;
  };

  const applySavedNick = useCallback((key) => {
    const saved = storageGet(nickStorageKey(key));
    if (saved === null) return;
    setLayout((prev) => ({ ...prev, texts: prev.texts.map((t) => withAutoTime(t, saved)) }));
  }, []);

  useEffect(() => {
    const saved = storageGet(STORAGE_PLATFORM_KEY);
    const key = PLATFORMS.some((p) => p.key === saved) ? saved : PLATFORMS[0].key;
    setPlatform(key);
    applySavedNick(key);
  }, [applySavedNick]);

  const selectPlatform = (key) => {
    setPlatform(key);
    storageSet(STORAGE_PLATFORM_KEY, key);
    applySavedNick(key);
  };

  // ---------- 레이아웃 갱신 헬퍼 ----------
  const updateLayer = useCallback((id, patch) => {
    setLayout((prev) => patchLayout(prev, id, patch));
  }, []);

  const changeNick = (id, value) => {
    updateLayer(id, (cur) => withAutoTime(cur, value));
    storageSet(nickStorageKey(platform), value);
  };

  // 시간 삭제 ↔ 시간 삽입 토글
  const toggleTime = (id) => {
    updateLayer(id, (cur) =>
      cur.time ? { time: "", timeRemoved: true } : { time: nowString(), timeRemoved: false },
    );
    setSelected(id);
  };

  // 문구 요소 추가: 마지막 요소의 문구·시간·스타일을 복사해 바로 위에 배치
  const addText = () => {
    setLayout((prev) => {
      if (prev.texts.length >= MAX_TEXTS) return prev;
      const last = prev.texts[prev.texts.length - 1] || createTextEntry();
      const entry = {
        ...createTextEntry(),
        nick: last.nick,
        time: last.time,
        timeRemoved: last.timeRemoved,
        x: last.x,
        y: clamp(last.y - last.size * 1.5, 0.05, 0.98),
        size: last.size,
        color: last.color,
        outline: last.outline,
      };
      setSelected(entry.id);
      return { ...prev, texts: [...prev.texts, entry] };
    });
  };

  const removeText = (id) => {
    setLayout((prev) => {
      if (prev.texts.length <= 1) return prev;
      return { ...prev, texts: prev.texts.filter((t) => t.id !== id) };
    });
    setSelected((s) => (s === id ? null : s));
  };

  const resetLayer = (id) => {
    setLayout((prev) => {
      const def = createDefaultLayout();
      if (isTextId(id)) {
        const index = prev.texts.findIndex((t) => t.id === id);
        const fresh = createTextEntry(clamp(0.92 - index * 0.06, 0.05, 0.98));
        return patchLayout(prev, id, (cur) => ({
          ...fresh,
          id: cur.id,
          nick: cur.nick,
          time: cur.time,
          timeRemoved: cur.timeRemoved,
        }));
      }
      return { ...prev, [id]: def[id] };
    });
  };

  const resetAll = () => {
    setLayout((prev) => {
      const def = createDefaultLayout();
      def.atPrefix = prev.atPrefix;
      def.logo.visible = prev.logo.visible;
      // 문구 내용은 유지하고 위치·크기·색만 초기화
      def.texts = prev.texts.map((t, i) => ({
        ...createTextEntry(clamp(0.92 - i * 0.06, 0.05, 0.98)),
        id: t.id,
        nick: t.nick,
        time: t.time,
        timeRemoved: t.timeRemoved,
      }));
      return def;
    });
    setSelected(null);
  };

  // 선택 레이어를 캔버스 픽셀 단위로 이동
  const moveLayer = useCallback((id, ddx, ddy) => {
    const canvas = canvasRef.current;
    const boxes = boxesRef.current;
    if (!canvas || !boxes || !id) return;
    const W = canvas.width;
    const H = canvas.height;
    setLayout((prev) => {
      const layer = getLayer(prev, id);
      if (!layer) return prev;
      if (id === "left" || id === "right") {
        const slot = boxes[id];
        const lim = (layer.zoom - 1) / 2;
        return {
          ...prev,
          [id]: {
            ...layer,
            dx: clamp(layer.dx + ddx / slot.w, -lim, lim),
            dy: clamp(layer.dy + ddy / slot.h, -lim, lim),
          },
        };
      }
      return patchLayout(prev, id, {
        x: clamp(layer.x + ddx / W, 0, 1),
        y: clamp(layer.y + ddy / H, 0, 1),
      });
    });
  }, []);

  // 선택 레이어 크기를 배율로 조절 (핀치)
  const scaleLayer = useCallback((id, factor) => {
    if (!id) return;
    setLayout((prev) => {
      const layer = getLayer(prev, id);
      if (!layer) return prev;
      if (id === "left" || id === "right") {
        const zoom = clamp(layer.zoom * factor, 1, 3);
        const lim = (zoom - 1) / 2;
        return {
          ...prev,
          [id]: { zoom, dx: clamp(layer.dx, -lim, lim), dy: clamp(layer.dy, -lim, lim) },
        };
      }
      if (id === "logo") {
        return { ...prev, logo: { ...layer, size: clamp(layer.size * factor, 0.1, 1) } };
      }
      return patchLayout(prev, id, { size: clamp(layer.size * factor, 0.02, 0.12) });
    });
  }, []);

  // ---------- 그리기 ----------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !ready) return;
    try {
      boxesRef.current = drawScene(canvas, images, layout, selected);
      setErrorMsg("");
    } catch (err) {
      console.error("[draw] failed", err);
      setErrorMsg(
        `미리보기 그리기에 실패했습니다 (${err && err.message ? err.message : "unknown"}). 다른 사진을 시도해 주십시오.`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftImg, rightImg, centerImg, layout, selected, ready]);

  // 선택 표시 없이 그린 뒤 JPEG로 인코딩 (Blob 우선, 실패 시 dataURL)
  const exportImage = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !ready) return null;
    drawScene(canvas, images, layout, null);
    const blob = await new Promise((resolve) => {
      try {
        canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92);
      } catch {
        resolve(null);
      }
    });
    boxesRef.current = drawScene(canvas, images, layout, selected);
    if (blob) return { url: URL.createObjectURL(blob), isBlob: true };
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    if (!dataUrl || dataUrl.length < 200 || dataUrl === "data:,") {
      throw new Error("empty dataUrl");
    }
    return { url: dataUrl, isBlob: false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftImg, rightImg, centerImg, layout, selected, ready]);

  const download = async () => {
    if (!ready || exporting) return;
    setExporting(true);
    try {
      const out = await exportImage();
      if (!out) return;
      const a = document.createElement("a");
      a.href = out.url;
      a.download = timeSetForImageName() + ".jpg";
      document.body.appendChild(a);
      a.click();
      a.remove();
      if (out.isBlob) setTimeout(() => URL.revokeObjectURL(out.url), 10000);
      setErrorMsg("");
    } catch (err) {
      console.error("[download] failed", err);
      setErrorMsg(
        `저장에 실패했습니다 (${err && err.message ? err.message : "unknown"}). 다른 사진을 시도하거나 이 메시지를 캡처해 개발자에게 전달해 주십시오.`,
      );
    } finally {
      setExporting(false);
    }
  };

  // iOS: 꾹 눌러 저장할 수 있는 결과 이미지를 편집이 멈춘 뒤 갱신
  useEffect(() => {
    if (!isIOS || !ready || isDragging) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const out = await exportImage();
        if (cancelled || !out) return;
        setResultUrl((prev) => {
          if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
          return out.url;
        });
      } catch (err) {
        console.error("[ios preview] failed", err);
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [exportImage, ready, isDragging]);

  // ---------- 포인터(터치/마우스) ----------
  const toCanvasPoint = (e) => {
    const canvas = canvasRef.current;
    const r = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) * canvas.width) / r.width,
      y: ((e.clientY - r.top) * canvas.height) / r.height,
    };
  };

  const flushPending = useCallback(() => {
    rafRef.current = null;
    const p = pendingRef.current;
    pendingRef.current = null;
    if (!p) return;
    if (p.ddx || p.ddy) moveLayer(p.id, p.ddx, p.ddy);
    if (p.factor && p.factor !== 1) scaleLayer(p.id, p.factor);
  }, [moveLayer, scaleLayer]);

  const queueGesture = (id, ddx, ddy, factor) => {
    const p = pendingRef.current || { id, ddx: 0, ddy: 0, factor: 1 };
    p.id = id;
    p.ddx += ddx;
    p.ddy += ddy;
    p.factor *= factor;
    pendingRef.current = p;
    if (!rafRef.current) rafRef.current = requestAnimationFrame(flushPending);
  };

  const pinchDistance = () => {
    const pts = [...pointersRef.current.values()];
    if (pts.length < 2) return 0;
    return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
  };

  const onPointerDown = (e) => {
    if (!ready) return;
    const p = toCanvasPoint(e);
    pointersRef.current.set(e.pointerId, p);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* 일부 브라우저는 이미 끝난 포인터에 대해 throw */
    }
    if (pointersRef.current.size === 1) {
      const id = hitTest(boxesRef.current, p.x, p.y);
      setSelected(id);
      gestureRef.current = id ? { id, lastX: p.x, lastY: p.y, pinchDist: 0 } : null;
      if (id) setIsDragging(true);
    } else if (gestureRef.current) {
      gestureRef.current.pinchDist = pinchDistance();
    }
  };

  const onPointerMove = (e) => {
    const g = gestureRef.current;
    if (!g || !pointersRef.current.has(e.pointerId)) return;
    const p = toCanvasPoint(e);
    pointersRef.current.set(e.pointerId, p);
    if (pointersRef.current.size >= 2) {
      const d = pinchDistance();
      if (g.pinchDist > 0 && d > 0) queueGesture(g.id, 0, 0, d / g.pinchDist);
      g.pinchDist = d;
      return;
    }
    if (e.pointerId !== [...pointersRef.current.keys()][0]) return;
    queueGesture(g.id, p.x - g.lastX, p.y - g.lastY, 1);
    g.lastX = p.x;
    g.lastY = p.y;
  };

  const onPointerUp = (e) => {
    pointersRef.current.delete(e.pointerId);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    const g = gestureRef.current;
    if (!g) return;
    if (pointersRef.current.size === 0) {
      gestureRef.current = null;
      setIsDragging(false);
    } else {
      // 손가락 하나가 남으면 그 위치부터 다시 드래그
      const rest = [...pointersRef.current.values()][0];
      g.lastX = rest.x;
      g.lastY = rest.y;
      g.pinchDist = 0;
    }
  };

  const onKeyDown = (e) => {
    if (!selected || !canvasRef.current) return;
    const step = (e.shiftKey ? 0.02 : 0.005) * canvasRef.current.width;
    const map = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const d = map[e.key];
    if (!d) return;
    e.preventDefault();
    moveLayer(selected, d[0], d[1]);
  };

  const textLabel = (id) => {
    const index = layout.texts.findIndex((t) => t.id === id);
    if (index < 0) return "닉네임";
    return layout.texts.length > 1 ? `닉네임 ${index + 1}` : "닉네임";
  };
  const layerIds = [...FIXED_LAYERS, ...layout.texts.map((t) => t.id)];
  const layerLabel = (id) => (isTextId(id) ? textLabel(id) : FIXED_LABELS[id]);

  // ---------- 선택 레이어 컨트롤 ----------
  const renderControls = () => {
    const layer = selected ? getLayer(layout, selected) : null;
    if (!layer) {
      return (
        <div className="smi-hint">
          수정할 요소를 탭하여 위치·크기·투명도를 수정할 수 있습니다.
          <br />
          드래그로 이동, 두 손가락으로 크기 조절이 가능합니다.
        </div>
      );
    }
    if (selected === "left" || selected === "right") {
      return (
        <>
          <div className="smi-row">
            <label htmlFor="smi-zoom">확대</label>
            <input
              id="smi-zoom"
              type="range"
              min={100}
              max={300}
              value={Math.round(layer.zoom * 100)}
              onChange={(e) => scaleLayer(selected, Number(e.target.value) / 100 / layer.zoom)}
            />
            <span className="smi-value">{Math.round(layer.zoom * 100)}%</span>
          </div>
          <div className="smi-row">
            <span className="smi-label" />
            <button type="button" className="smi-btn smi-btn--ghost" onClick={() => resetLayer(selected)}>
              원래대로
            </button>
            <button type="button" className="smi-btn smi-btn--ghost" onClick={swapPhotos}>
              좌우 바꾸기
            </button>
          </div>
          <div className="smi-hint">
            {layer.zoom > 1
              ? "사진을 드래그하면 보이는 위치가 조절됩니다."
              : "확대하면 사진을 드래그해 위치를 조절할 수 있습니다."}
          </div>
        </>
      );
    }
    if (selected === "logo") {
      return (
        <>
          <div className="smi-row">
            <label htmlFor="smi-logo-size">크기</label>
            <input
              id="smi-logo-size"
              type="range"
              min={10}
              max={100}
              value={Math.round(layer.size * 100)}
              onChange={(e) => updateLayer("logo", { size: Number(e.target.value) / 100 })}
            />
            <span className="smi-value">{Math.round(layer.size * 100)}%</span>
          </div>
          <div className="smi-row">
            <label htmlFor="smi-logo-opacity">투명도</label>
            <input
              id="smi-logo-opacity"
              type="range"
              min={0}
              max={100}
              value={Math.round(layer.opacity * 100)}
              onChange={(e) => updateLayer("logo", { opacity: Number(e.target.value) / 100 })}
            />
            <span className="smi-value">{Math.round(layer.opacity * 100)}%</span>
          </div>
          <div className="smi-row">
            <span className="smi-label" />
            <button type="button" className="smi-btn smi-btn--ghost" onClick={() => resetLayer("logo")}>
              가운데로
            </button>
          </div>
        </>
      );
    }
    // 문구 요소
    return (
      <>
        <div className="smi-row">
          <label htmlFor="smi-text-size">크기</label>
          <input
            id="smi-text-size"
            type="range"
            min={20}
            max={120}
            value={Math.round(layer.size * 1000)}
            onChange={(e) => updateLayer(selected, { size: Number(e.target.value) / 1000 })}
          />
          <span className="smi-value">{Math.round(layer.size * 1000)}</span>
        </div>
        <div className="smi-row">
          <span className="smi-label">색상</span>
          {TEXT_PRESETS.map((p) => (
            <button
              key={p.color}
              type="button"
              className={`smi-swatch${layer.color === p.color ? " is-active" : ""}`}
              style={{ background: p.color }}
              aria-label={p.label}
              title={p.label}
              onClick={() => updateLayer(selected, { color: p.color })}
            />
          ))}
          <input
            type="color"
            aria-label="직접 선택"
            value={layer.color}
            onChange={(e) => updateLayer(selected, { color: e.target.value })}
          />
        </div>
        <div className="smi-row">
          <span className="smi-label" />
          <label className="smi-check">
            <input
              type="checkbox"
              checked={layer.outline}
              onChange={(e) => updateLayer(selected, { outline: e.target.checked })}
            />{" "}
            테두리
          </label>
          <button type="button" className="smi-btn smi-btn--ghost" onClick={() => resetLayer(selected)}>
            위치 초기화
          </button>
        </div>
        {!formatTextEntry(layer, layout.atPrefix) && (
          <div className="smi-hint">닉네임을 입력하면 미리보기에 표시됩니다.</div>
        )}
      </>
    );
  };

  const fileButton = (key, label) => (
    <label className={`smi-file-btn${images[key] ? " is-set" : ""}`}>
      <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, key)} />
      {fileNames[key] ? `${label}: ${fileNames[key]}` : `${label} 선택`}
    </label>
  );

  return (
    <div className="smi">
      <h2 className="smi-title">스트리밍 이미지 메이커</h2>
      <div className="smi-notice">
        삼성 인터넷 브라우저에서 다운로드 오류가 발생 할 수 있습니다.
        <br />
        크롬 브라우저 사용을 권장합니다.
      </div>

      <div className="smi-panel">
        <div className="smi-panel-title smi-panel-title--row">
          <span>1. 사진 고르기</span>
          <Toggle
            label="로고 표시"
            checked={layout.logo.visible !== false}
            onChange={(on) => updateLayer("logo", { visible: on })}
          />
        </div>
        <div className="smi-files">
          {fileButton("left", "좌측 사진")}
          {fileButton("right", "우측 사진")}
          {fileButton("center", "로고")}
        </div>
        <div className="smi-hint">※ 로고 미업로드 시 QWER 로고가 적용됩니다. 각 파일 최대 10MB.</div>
      </div>

      <div className="smi-panel">
        <div className="smi-panel-title smi-panel-title--row">
          <span>2. 문구 넣기</span>
          <Toggle
            label="닉네임 앞에 @ 표시"
            checked={!!layout.atPrefix}
            onChange={(on) => setLayout((prev) => ({ ...prev, atPrefix: on }))}
          />
        </div>
        <div className="smi-row">
          <span className="smi-label">플랫폼</span>
          <div className="smi-chips smi-chips--inline" role="tablist" aria-label="문구를 저장할 플랫폼">
            {PLATFORMS.map((p) => (
              <button
                key={p.key}
                type="button"
                role="tab"
                aria-selected={platform === p.key}
                className={`smi-chip${platform === p.key ? " is-active" : ""}`}
                onClick={() => selectPlatform(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="smi-hint">
          닉네임은 플랫폼별로 이 브라우저에 저장되어 다음에 자동으로 채워집니다.
        </div>

        {layout.texts.map((entry, index) => (
          <div key={entry.id} className={`smi-entry${selected === entry.id ? " is-active" : ""}`}>
            <div className="smi-row">
              <label htmlFor={`smi-nick-${entry.id}`}>{textLabel(entry.id)}</label>
              <input
                id={`smi-nick-${entry.id}`}
                type="text"
                value={entry.nick}
                placeholder="닉네임 입력"
                onFocus={() => setSelected(entry.id)}
                onChange={(e) => changeNick(entry.id, e.target.value)}
              />
              <Toggle
                label="시간"
                ariaLabel={`${textLabel(entry.id)} 시간 표시`}
                checked={!!entry.time}
                onChange={(on) => {
                  if (on !== !!entry.time) toggleTime(entry.id);
                }}
              />
              {layout.texts.length > 1 && (
                <button
                  type="button"
                  className="smi-btn smi-btn--ghost smi-btn--remove"
                  aria-label={`${textLabel(entry.id)} 요소 삭제`}
                  title="이 요소 삭제"
                  onClick={() => removeText(entry.id)}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        ))}
        <div className="smi-row">
          <span className="smi-label" />
          <button
            type="button"
            className="smi-btn smi-btn--ghost"
            onClick={addText}
            disabled={layout.texts.length >= MAX_TEXTS}
          >
            + 요소 추가
          </button>
        </div>
        <div className="smi-hint">
          닉네임을 넣으면 현재 시간이 자동으로 붙어 "@닉네임 / 시간" 형태로 표시됩니다. 요소를 추가하면 마지막
          요소가 복사됩니다. (최대 {MAX_TEXTS}개)
        </div>
      </div>

      <div className="smi-panel">
        <div className="smi-panel-title">3. 위치·크기 다듬기 (미리보기)</div>
        <div className="smi-stage" tabIndex={ready ? 0 : -1} onKeyDown={onKeyDown}>
          <canvas
            ref={canvasRef}
            className={`smi-canvas${isDragging ? " is-dragging" : ""}`}
            style={{ display: ready ? "block" : "none" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            aria-label="합성 미리보기. 드래그로 위치를 옮길 수 있습니다."
          />
          {!ready && (
            <div className="smi-placeholder">
              좌측·우측 사진을 올리고
              <br />
              수정할 요소를 탭하여
              <br />
              위치·크기·투명도 수정
            </div>
          )}
        </div>
        <div className="smi-chips" role="tablist" aria-label="편집할 대상">
          {layerIds.map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={selected === id}
              className={`smi-chip${selected === id ? " is-active" : ""}`}
              disabled={!ready}
              onClick={() => setSelected((s) => (s === id ? null : id))}
            >
              {layerLabel(id)}
            </button>
          ))}
        </div>
        {renderControls()}
        {errorMsg && <div className="smi-error">{errorMsg}</div>}
      </div>

      <div className="smi-actions">
        <button type="button" className="smi-btn smi-btn--ghost" onClick={resetAll} disabled={!ready}>
          전체 초기화
        </button>
        <button
          type="button"
          className="smi-btn smi-btn--download"
          onClick={download}
          disabled={!ready || exporting}
        >
          {exporting ? "저장 중..." : "다운로드"}
        </button>
      </div>

      {isIOS && ready && resultUrl && (
        <div className="smi-ios-preview">
          <div className="smi-footnote">iOS는 아래 이미지를 길게 눌러 저장</div>
          <img src={resultUrl} alt="합성 결과" />
        </div>
      )}
      <div className="smi-footnote">
        <Link to="/images/legacy">구버전 스밍짤 만들기로 가기</Link>
      </div>
    </div>
  );
}

export default StreamingImageMerger;
